import { FmButton } from '../FmButton';
import { FmHeading, FmInline, FmText } from '../FmComposition';
import { FmSelector } from '../FmSelector';
import type {
  FmAnalysisTimeSeriesTransport,
  FmAnalysisWorkspaceDescriptor,
} from '../FmAnalysisWorkspaceRoot.types';
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';
export function AnalysisWorkspaceLayerChips({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  return (
    <FmSelector
      variant="segmented"
      className="fm-analysis-workspace__layers"
      label={descriptor.copy.viewsLabel}
      value={descriptor.activeViewId}
      onChange={descriptor.onViewChange}
      items={descriptor.views.map((view) => ({
        value: view.id,
        label: view.label,
      }))}
      data-testid="analysis-workspace-layers"
    />
  );
}

export function AnalysisWorkspaceTransport({
  descriptor,
  transport,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  transport: FmAnalysisTimeSeriesTransport;
}) {
  const actionsById = new Map(descriptor.actions.map((action) => [action.id, action]));
  const playId = transport.playing ? transport.stopActionId : transport.playActionId;
  const play = actionsById.get(playId);
  const previous = actionsById.get(transport.previousActionId);
  const next = actionsById.get(transport.nextActionId);
  return (
    <div
      className="fm-analysis-workspace__transport"
      {...fmMapOcclusion('bottom', 'panel')}
      data-testid="analysis-workspace-transport"
    >
      <div className="fm-analysis-workspace__transport-controls">
        <FmInline gap="sm" wrap={false}>
          {previous ? (
            <FmButton
              variant="default"
              disabled={Boolean(previous.disabledReason)}
              title={previous.disabledReason}
              onClick={() => descriptor.onAction(previous.id)}
            >
              {previous.label}
            </FmButton>
          ) : null}
          {play ? (
            <FmButton
              variant="primary"
              disabled={Boolean(play.disabledReason)}
              title={play.disabledReason}
              onClick={() => descriptor.onAction(play.id)}
            >
              {play.label}
            </FmButton>
          ) : null}
          {next ? (
            <FmButton
              variant="default"
              disabled={Boolean(next.disabledReason)}
              title={next.disabledReason}
              onClick={() => descriptor.onAction(next.id)}
            >
              {next.label}
            </FmButton>
          ) : null}
        </FmInline>
        <FmText as="span" size="caption" data-testid="analysis-workspace-series-count">
          {transport.positionLabel}
        </FmText>
      </div>
      <AnalysisWorkspaceLayerChips descriptor={descriptor} />
      <div
        className="fm-analysis-workspace__rail"
        aria-label={transport.positionLabel}
        data-testid="analysis-workspace-rail"
      >
        {transport.frames.map((frame) => {
          const active = frame.id === transport.activeId;
          return (
            <button
              key={frame.id}
              type="button"
              aria-current={active ? 'true' : undefined}
              className={[
                'fm-analysis-workspace__tick',
                active && 'fm-analysis-workspace__tick--active',
              ]
                .filter(Boolean)
                .join(' ')}
              title={frame.label}
              onClick={() => descriptor.onSelect('source', frame.id)}
            >
              <span className="fm-analysis-workspace__tick-mark" />
              <span className="fm-analysis-workspace__tick-label">{frame.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AnalysisWorkspaceDateChip({
  title,
  label,
}: {
  title?: string;
  label: string;
}) {
  return (
    <div className="fm-analysis-workspace__date-chip" data-testid="analysis-workspace-date">
      {title ? (
        <FmHeading level={1} visualLevel={5}>
          {title}
        </FmHeading>
      ) : null}
      {label ? <FmText as="span">{label}</FmText> : null}
    </div>
  );
}
